import { createContext, useContext, type BaseSyntheticEvent, type ReactElement, type ReactNode } from 'react';
import {
  useController,
  useForm,
  type Control,
  type FieldError,
  type FieldValues,
  type Path,
  type RegisterOptions,
  type SubmitHandler,
  type UseFormProps,
  type UseFormReturn,
} from 'react-hook-form';
import { Alert } from '../Alert/Alert';
import { Combobox, type SingleComboboxProps } from '../Combobox/Combobox';
import { Checkbox, SelectField, TextArea, TextField, type CheckboxProps, type SelectFieldProps, type TextAreaProps, type TextFieldProps } from '../Field/Field';
import { MarkdownField, type MarkdownFieldProps } from '../Markdown/MarkdownField';
import { applyServerErrors, SERVER_ERROR, type ServerOutcome } from './server-errors';

/**
 * Формы на react-hook-form (ADR-011, SYS-UC-12). Значения живут в форме, а не в useState
 * страницы; поля связаны с ней через useController, поэтому ввод не пересоздаёт соседние поля.
 */
export function useAppForm<T extends FieldValues>(props: UseFormProps<T>): UseFormReturn<T> {
  return useForm<T>({ mode: 'onTouched', ...props });
}

/**
 * Обработчик отправки. Ошибки прошлой отправки снимаются заранее: сервер ставит их и на пути без
 * зарегистрированного поля (строка списка), и handleSubmit сам бы их не снял — форма бы не ушла.
 */
export function submitHandler<T extends FieldValues>(form: UseFormReturn<T>, onValid: SubmitHandler<T>): (event?: BaseSyntheticEvent) => Promise<void> {
  return async (event) => {
    form.clearErrors();
    await form.handleSubmit(onValid)(event);
  };
}

export interface ServerFormOptions<T extends FieldValues, R extends ServerOutcome = ServerOutcome> extends UseFormProps<T> {
  /** Поля формы: ошибки сервера с этими путями встают к полям, остальные — общей ошибкой. */
  fields: readonly string[];
  /** Отправка: подключённый компонент вызывает API и возвращает его ответ. */
  onSubmit(values: T): Promise<R>;
  /**
   * Значения после успешной отправки: очистить поле ввода или взять то, что сохранил сервер
   * (нормализованные теги). Не задано — форма не сбрасывается.
   */
  afterSuccess?(values: T, outcome: R): T;
}

/**
 * Форма, которая сама владеет своим состоянием и раскладывает ответ сервера по полям (как
 * LoginForm в jm). Визуальный компонент получает только onSubmit и не знает об API, поэтому
 * его можно показать в витрине во всех состояниях, включая ошибки сервера.
 */
export function useServerForm<T extends FieldValues, R extends ServerOutcome = ServerOutcome>({ fields, onSubmit, afterSuccess, ...props }: ServerFormOptions<T, R>): {
  form: UseFormReturn<T>;
  submit: (event?: BaseSyntheticEvent) => Promise<void>;
} {
  const form = useAppForm<T>(props);
  const submit = submitHandler(form, async (values) => {
    const outcome = await onSubmit(values);
    if (applyServerErrors(form.setError, outcome, fields) && afterSuccess) form.reset(afterSuccess(values, outcome));
  });
  return { form, submit };
}

/** Текст ошибки по коду (ErrorCode или код проверки поля). ui-kit не знает словарей — его даёт приложение. */
type MessageOf = (code: string) => string;

const FormMessagesContext = createContext<MessageOf>((code) => code);

export function FormMessagesProvider({ messageOf, children }: { messageOf: MessageOf; children: ReactNode }): ReactElement {
  return <FormMessagesContext.Provider value={messageOf}>{children}</FormMessagesContext.Provider>;
}

/** Ошибка поля, строки списка или всего списка — у всех есть message с кодом. */
type ErrorLike = Pick<FieldError, 'message'> | { message?: unknown } | undefined;

export function useFormMessage(): (error: ErrorLike) => string | null {
  const messageOf = useContext(FormMessagesContext);
  return (error) => (typeof error?.message === 'string' && error.message ? messageOf(error.message) : null);
}

/** Общая ошибка формы — то, что сервер не отнёс к полю (applyServerErrors). */
export function FormAlert<T extends FieldValues>({ form }: { form: UseFormReturn<T> }): ReactElement | null {
  const message = useFormMessage();
  const text = message(form.formState.errors.root?.[SERVER_ERROR]);
  return text ? <Alert tone="danger">{text}</Alert> : null;
}

interface Bound<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  rules?: RegisterOptions<T, Path<T>>;
  /** После изменения значения — например, пересчитать зависимое поле. */
  onValueChange?(value: string): void;
}

type Controlled = 'name' | 'value' | 'onChange' | 'onBlur' | 'error' | 'ref';

export function FormTextField<T extends FieldValues>({ control, name, rules, onValueChange, ...rest }: Bound<T> & Omit<TextFieldProps, Controlled>): ReactElement {
  const { field, fieldState } = useController({ control, name, rules });
  const message = useFormMessage();
  return (
    <TextField
      {...rest}
      ref={field.ref}
      name={field.name}
      value={(field.value as string | undefined) ?? ''}
      onBlur={field.onBlur}
      onChange={(event) => {
        field.onChange(event.target.value);
        onValueChange?.(event.target.value);
      }}
      error={message(fieldState.error)}
    />
  );
}

export function FormTextArea<T extends FieldValues>({ control, name, rules, onValueChange, ...rest }: Bound<T> & Omit<TextAreaProps, Controlled>): ReactElement {
  const { field, fieldState } = useController({ control, name, rules });
  const message = useFormMessage();
  return (
    <TextArea
      {...rest}
      ref={field.ref}
      name={field.name}
      value={(field.value as string | undefined) ?? ''}
      onBlur={field.onBlur}
      onChange={(event) => {
        field.onChange(event.target.value);
        onValueChange?.(event.target.value);
      }}
      error={message(fieldState.error)}
    />
  );
}

/** Текст в Markdown с предпросмотром (правила оформления, заметки — ADR-016). */
export function FormMarkdownField<T extends FieldValues>({ control, name, rules, onValueChange, ...rest }: Bound<T> & Omit<MarkdownFieldProps, Controlled>): ReactElement {
  const { field, fieldState } = useController({ control, name, rules });
  const message = useFormMessage();
  return (
    <MarkdownField
      {...rest}
      ref={field.ref}
      name={field.name}
      value={(field.value as string | undefined) ?? ''}
      onBlur={field.onBlur}
      onChange={(event) => {
        field.onChange(event.target.value);
        onValueChange?.(event.target.value);
      }}
      error={message(fieldState.error)}
    />
  );
}

export function FormSelectField<T extends FieldValues>({ control, name, rules, onValueChange, ...rest }: Bound<T> & Omit<SelectFieldProps, Controlled>): ReactElement {
  const { field, fieldState } = useController({ control, name, rules });
  const message = useFormMessage();
  return (
    <SelectField
      {...rest}
      ref={field.ref}
      name={field.name}
      value={(field.value as string | undefined) ?? ''}
      onBlur={field.onBlur}
      onChange={(event) => {
        field.onChange(event.target.value);
        onValueChange?.(event.target.value);
      }}
      error={message(fieldState.error)}
    />
  );
}

export function FormCheckbox<T extends FieldValues>({ control, name, rules, ...rest }: Omit<Bound<T>, 'onValueChange'> & Omit<CheckboxProps, Controlled | 'checked'>): ReactElement {
  const { field } = useController({ control, name, rules });
  return <Checkbox {...rest} ref={field.ref} name={field.name} checked={field.value === true} onBlur={field.onBlur} onChange={(event) => field.onChange(event.target.checked)} />;
}

/** Автокомплит в форме: одно значение из списка (WS-RULE-2, ENT-UC-8). */
export function FormCombobox<T extends FieldValues>({
  control,
  name,
  rules,
  ...rest
}: Omit<Bound<T>, 'onValueChange'> & Omit<SingleComboboxProps, 'value' | 'onChange' | 'error' | 'multiple'>): ReactElement {
  const { field, fieldState } = useController({ control, name, rules });
  const message = useFormMessage();
  return <Combobox {...rest} value={(field.value as string | null | undefined) ?? null} onChange={(value) => field.onChange(value)} error={message(fieldState.error)} />;
}
